import { Attachment, Chat, Document, Message, User } from '@graphql/generated';
import { generateTagCollections } from '@utils/tagCollectionService';

type UseChatCardDataParams = {
  chat: Chat;
  messageData?: Message[];
  me?: User;
  isGroupChat: boolean;
  isFromSearch?: boolean;
  isFromChatSearch?: boolean;
  isFromEditChat?: boolean;
};

export const isDocumentType = (item: Attachment): item is Document =>
  item.__typename === 'Document';

export const getMessageBody = (body: any) => {
  const safeBody = body || '';
  const regex = /{@}\[([^\[\]()]+?)\]/g;
  const matches = [...safeBody.matchAll(regex)];
  const modifiedText = matches.reduce((acc: string, match: any) => {
    const extractedContent = match[1];
    const replacedString = acc.replace(match[0], extractedContent);
    const output = replacedString.replace(/\([^()]*\)/, '').trim();
    return output;
  }, safeBody);
  return modifiedText;
};

export const useChatCardData = ({
  chat,
  messageData,
  me,
  isGroupChat,
  isFromSearch = false,
  isFromChatSearch = false,
  isFromEditChat = false,
}: UseChatCardDataParams) => {
  const { friendRequestAccepted, ownerId, messageBody } = chat;

  // Attachment filtering
  const documentAttachments: Document[] = messageData?.[0]?.attachments
    ? (messageData[0].attachments.filter((item) => {
        return isDocumentType(item);
      }) as Document[])
    : [];

  const audioAttachments = documentAttachments?.filter((item) => item.isAudio);
  const imageAttachments = documentAttachments?.filter((item) => item.isImage);
  const fileAttachments = documentAttachments?.filter(
    (item) => !item.isImage && !item.isAudio
  );
  const hideIcon = !!(
    (imageAttachments && imageAttachments.length > 0) ||
    (fileAttachments && fileAttachments.length > 0) ||
    (audioAttachments && audioAttachments.length > 0)
  );

  // Tag collection generation
  const tagsByAuthorCollections =
    messageData && generateTagCollections(messageData[0]?.attachments || []);

  // Self-chat detection
  // Prefer users if present (from detailed queries), fallback to userIds (from list pagination)
  const otherUser =
    !isGroupChat && chat.users
      ? chat.users.find((user) => user.id !== me?.id)
      : undefined;
  const otherUserIdFromUserIds =
    !isGroupChat && chat.userIds
      ? chat.userIds.find((id) => id !== me?.id)
      : undefined;
  const isSelfChat =
    !isGroupChat &&
    !!me?.id &&
    !!(chat.users?.length || chat.userIds?.length) &&
    !otherUser &&
    !otherUserIdFromUserIds;

  const isPendingSingleUserChat =
    !isGroupChat &&
    ((chat?.userIds?.length ?? 0) || (chat.users?.length ?? 0)) === 1 &&
    (chat?.pendingInvitations?.length ?? 0) === 1 &&
    chat?.pendingInvitations[0].email === chat?.name;

  // Friend request detection (union of all checks from both platforms)
  const isProjectEditorRequest =
    messageBody === 'New Request to Become a Project Editor.';
  const isSenderFriendRequest =
    !friendRequestAccepted &&
    ownerId === me?.id &&
    !isGroupChat &&
    !isFromSearch &&
    !isFromChatSearch &&
    !isFromEditChat &&
    !isProjectEditorRequest;
  const isPendingInviteChat = isPendingSingleUserChat || isSenderFriendRequest;

  return {
    documentAttachments,
    audioAttachments,
    imageAttachments,
    fileAttachments,
    hideIcon,
    tagsByAuthorCollections,
    otherUser,
    otherUserIdFromUserIds,
    isSelfChat,
    isPendingInviteChat,
    isSenderFriendRequest,
  };
};
